import React, { useEffect, useState } from "react"
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, TextField, IconButton, Button, Typography, Collapse,
  Box
} from "@mui/material"
import { Delete, Add, Edit, Check, ExpandMore, ExpandLess, Close, ForwardToInbox } from "@mui/icons-material"
import { fieldsValidations } from "../../services/fieldsValidations"
import ErrorField from "./Fields/ErrorField"
import { ApiFetch } from "../../Utils/ApiFetch"
import useGlobalContext from "../../Hooks/useGlobalContext"
import { toast, ToastContainer } from 'material-react-toastify';
import ModalConfirmOrDecline from "../Feature/ModalConfirmOrDecline"
import { translateFunc } from "../../Helpers/translateFunc"

export const AddableList = ({ valueArray, name, label, fields, onChange, objectType, pk, t }) => {
  const globalContext = useGlobalContext()
  
  const fieldsInOrder = fields.sort((a, b) => a.nro - b.nro)
  const rowsName = fieldsInOrder.map(field => field.name)
  const newObject = fieldsInOrder.reduce((acc, { name }) => ({ ...acc, [name]: "" }), {key: -1, deleted: false})
  
  const [rows, setRows] = useState([])
  const [newRow, setNewRow] = useState(newObject)
  const [rowsEnabledForResend, setRowsEnabledForResend] = useState([])
  const [open, setOpen] = useState(true)
  const [indexRowEditing, setIndexRowEditing] = useState(null)
  const [fieldsError, setFieldsError] = useState({})
  const [rowModalAction, setRowModalAction] = useState({modal: false, index: null, type: null})

  const handleChange = (field, value) => {
    setNewRow(prev => ({ ...prev, [field]: value }))
  }

  useEffect(()=>{
    const index = rows.findIndex(item => item.isEditing === true)
    const rowIndex = index === -1 ? null : index
    setIndexRowEditing(rowIndex)
  },[rows])

  useEffect(()=>{
    if(pk) {
      ApiFetch(
        `authorizations/auth-to-resend/${objectType}/${pk}`,
        globalContext,
        true,
        async (body) => {
          let resJson = await body.json()
          setRowsEnabledForResend(resJson)
        },
        error => {
          console.log(error)
          setRowsEnabledForResend([])
        },
        {
          method: "GET",
        }
      );
    }

    setRows(valueArray ? JSON.parse(valueArray) : rows)
  },[])

  const fieldHasErrors = async (filteredData, index) => {
    let fieldErrorTemp = {row: index}

    for (const field of Object.keys(filteredData)) {
      const fieldToValidate = fields.find(obj => obj.name === field)
      fieldErrorTemp[fieldToValidate.name] = { value: false, messages: [] }

      if (fieldToValidate) {
        const hasError = await fieldsValidations(
          filteredData[fieldToValidate.name],
          fieldToValidate.validations,
          {
            fieldType: fieldToValidate.type,
            fieldName: fieldToValidate.name,
          }
        )

        if (hasError.length !== 0) {
          fieldErrorTemp[fieldToValidate.name] = { value: true, messages: hasError }
        }
      }
    }

    setFieldsError(fieldErrorTemp)
    return fieldErrorTemp
  }

  const addRow = async () => {
    const filteredData = Object.fromEntries(
      Object.entries(newRow).filter(([key]) => rowsName.includes(key))
    )

    const fieldErrors = await fieldHasErrors(filteredData, -1)

    const hasErrors = Object.values(fieldErrors).some(error => error.value)

    if (hasErrors) return

    const rowAdded = [...rows, { key: -1, ...filteredData, original: {}, isEditing: false}]
    setRows(rowAdded)
    setNewRow(newObject)
    
    onChange(JSON.stringify(rowAdded.map((row) => {
      const {original, isEditing, ...dataRow} = row
      return dataRow
    })))
  }

  const handleDeleteRow = (index) => {
    setRowModalAction({modal: true, index, type: 'delete'})
  }

  const deleteRow = (bool, index) => {
    const rowsCleaned = [...rows];
    setRowModalAction({modal: false, index: null, type: null})

    if (rowsCleaned[index].key === -1) {
      rowsCleaned.splice(index, 1); // Elimina el elemento si key es -1 (nuevo)
    } else {
      rowsCleaned[index] = { ...rowsCleaned[index], deleted: true }; // Flag como eliminado (si hay que eliminarlo desde API)
    }

    setRows(rowsCleaned);

    onChange(JSON.stringify(rowsCleaned.map(({ original, isEditing, deleted, ...dataRow }) => 
      rowsCleaned[index].key === -1 ? dataRow : { ...dataRow, deleted }
    )));
  }

  const toggleEdit = (index) => {
    const newRows = [...rows]
    newRows[index].isEditing = true

    const filteredData = Object.fromEntries(
      Object.entries(newRows[index]).filter(([key]) => rowsName.includes(key))
    )

    newRows[index].original = filteredData
    setRows(newRows)
  }

  const sucessEdit = async (index) => {
    const newRows = [...rows]
    const objectRow = newRows[index]
    
    const filteredData = Object.fromEntries(
      Object.entries(objectRow).filter(([key]) => rowsName.includes(key))
    )

    const fieldErrors = await fieldHasErrors(filteredData, index)

    const hasErrors = Object.values(fieldErrors).some(error => error.value)

    if (hasErrors)  return

    newRows[index].original = {}
    newRows[index].isEditing = false

    setRows(newRows)
    onChange(JSON.stringify(newRows.map((row) => {
      const {original, isEditing, ...dataRow} = row
      return dataRow
    })))
  }

  const cancelEdit = (index) => {
    const newRows = [...rows]
    newRows[index] = {  key: newRows[index].key, ...newRows[index].original, isEditing: false, original: {} }
    setFieldsError({})
    setRows(newRows)
  }

  const handleResendEmail = (index) => {
    setRowModalAction({modal: true, index, type: 'resendEmail'})
  }

  const resendEmail = async (bool, index) => {
    const key = rows[index].key
    setRowModalAction({modal: false, index: null, type: null})

    key !== -1 && await ApiFetch(
      `authorizations/resend/${objectType}/${pk}/${key}`,
      globalContext,
      true,
      async (body) => {
        toast.success(t('stepByStep.button.addableList.toast.emailSuccess'), {
          className: 'toast-message-body',
          progressClassName: 'toast-message-progress',
          position: 'bottom-left',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      },
      error => {
        toast.error(t('stepByStep.button.addableList.toast.emailError'), {
          className: 'toast-message-body',
          position: 'bottom-left',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          progressStyle: { backgroundColor: '#D32F2F' }
        });
      },
      {
        method: "POST",
      }
    );
  }

  const renderCell = (field, value, handleChange, disabled) => {
    switch (field.type) {
      case 'text':
        return (<TextField
          variant="outlined"
          size="small"
          sx={{ "& fieldset": { border: "none" } }}
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          disabled={disabled}
          fullWidth
          error={!!fieldsError[field.name]?.value}
        />)
      case 'number':
        return (<TextField
          variant="outlined"
          size="small"
          type="number"
          sx={{
            "& fieldset": { border: "none" },
            "& input[type=number]": {
              MozAppearance: "textfield",
            },
            "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
              {
                WebkitAppearance: "none",
                margin: 0,
              },
          }}
          value={value}
          onChange={(e) => {
            const value = e.target.value
            if (/^\d*\.?\d*$/.test(value)) {
              handleChange(value)
            }
          }}
          disabled={disabled}
          fullWidth
          error={!!fieldsError[field.name]?.value}
        />)
      default:
        return null
    }
  }

  return (
    <>
      <ModalConfirmOrDecline
        open={ rowModalAction.modal }  
        modalText={rows[rowModalAction.index] ? `${t(
          rowModalAction.type === 'delete'
            ? 'stepByStep.button.addableList.modal.deleteMessage'
            : 'stepByStep.button.addableList.modal.resendMessage'
          )} ${[rows[rowModalAction.index]?.name, rows[rowModalAction.index]?.lastname]
            .filter(Boolean)
            .join(' ')}?`
          : ''
        }
        toggleModal={() => setRowModalAction({modal: false, index: null, type: null})}
        handleAction={ rowModalAction.type === 'delete' ? deleteRow : resendEmail }
        data={rowModalAction.index}
      />
      <ToastContainer />
      <TableContainer component={Paper}>
        <Typography
          variant="subtitle2"
          component="div"
          color="text.secondary"
          sx={{ display: "flex", alignItems: "center", paddingY: 1, paddingLeft: 2, cursor: "pointer" }}
          onClick={() => setOpen(!open)}
        >
          {label}
          <IconButton size="small" sx={{ marginLeft: "auto" }}>
            {open ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Typography>
        <Collapse in={open}>
          <Table>
            <TableHead sx={{ borderTop: "1px solid #ccc" }}>
              <TableRow>
                {fieldsInOrder.map(field => (
                  <TableCell key={`${field.label}-head`}>
                    {translateFunc(t, `basicFormStepByStep.fieldTranslate.addableList.${name}.${field.name}`, field.label)}
                  </TableCell>
                ))}
                <TableCell>{t('stepByStep.button.addableList.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => (
                !row.deleted ? (
                  <TableRow
                    key={`${index}-listItem`}
                    sx={{
                      backgroundColor: row.isEditing ? "transparent" : "#f5f5f5",
                    }}
                  >
                    {fieldsInOrder.map((field, indexRow) => (
                      <TableCell
                        key={`${field}-${indexRow}-list`}
                        sx={{
                          padding: 0,
                          paddingLeft: 1,
                          borderLeft: indexRow !== 0 ? "1px solid #ccc" : "none",
                        }}
                      >
                        {renderCell(field, row[field.name], (value) => {
                          const newRows = [...rows]
                          newRows[index][field.name] = value
                          setRows(newRows)
                        }, !row.isEditing)}
                        {fieldsError.row === index && fieldsError[field.name]?.value && (
                          <ErrorField errors={fieldsError[field.name]?.messages} t={t} />
                        )}
                      </TableCell>
                    ))}
                    <TableCell
                      sx={{
                        py: 0,
                        borderLeft: "1px solid #ccc",
                        textAlign: 'center'
                      }}
                    >
                      {row.isEditing ? (
                        <>
                          <IconButton disabled={(indexRowEditing !== null && indexRowEditing !== index)} onClick={() => sucessEdit(index)} color="success">
                            <Check />
                          </IconButton>
                          <IconButton disabled={(indexRowEditing !== null && indexRowEditing !== index)} onClick={() => cancelEdit(index)} color="error">
                            <Close />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <IconButton disabled={row.key !== -1 || (indexRowEditing !== null && indexRowEditing !== index)} onClick={() => toggleEdit(index)} color="primary">
                            <Edit />
                          </IconButton>
                          <IconButton disabled={(indexRowEditing !== null && indexRowEditing !== index)} onClick={() => handleDeleteRow(index)} color="error">
                            <Delete />
                          </IconButton>
                          {pk && 
                            <IconButton 
                              disabled={
                                !rowsEnabledForResend.some(auth => auth.id === row.key) || 
                                (indexRowEditing !== null && indexRowEditing !== index) ||
                                row.key === -1
                              } 
                              onClick={() => handleResendEmail(index)} 
                              color="primary"
                            >
                              <ForwardToInbox />
                            </IconButton>
                          }
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  <></>
                )
              ))}
            </TableBody>
            <TableRow
              sx={{
                backgroundColor: indexRowEditing !== null ? "#f5f5f5" : "transparent",
              }}
            >
              {fieldsInOrder.map((field, indexRow) => (
                <TableCell key={`${field.name}-${indexRow}-add`} sx={{ padding: 0, borderLeft: indexRow !== 0 ? "1px solid #ccc" : "none" }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      paddingLeft: 1
                    }}
                  >
                    {renderCell(field, newRow[field.name], (value) => handleChange(field.name, value), indexRowEditing !== null)}
                    {fieldsError.row === -1 && fieldsError[field.name]?.value && (
                      <ErrorField errors={fieldsError[field.name]?.messages} t={t} />
                    )}
                  </Box>
                </TableCell>
              ))}
              <TableCell 
                key={'button-add-addableList'} 
                sx={{ 
                  padding: 0, 
                  borderLeft: "1px solid #ccc",
                  textAlign: 'center'
                }}
              >
                <Button 
                  disabled={indexRowEditing !== null} 
                  onClick={addRow} 
                  startIcon={<Add />} 
                  variant="contained" 
                  sx={{
                    width: '90%'
                  }}
                >
                  {t('stepByStep.button.addableList.addrow')}
                </Button>
              </TableCell>
            </TableRow>
          </Table>
        </Collapse>
      </TableContainer>
    </>
  )
}

 